import {
  BuildingOUT,
  OrganisationOUT,
  OrganisationTypeOUT,
  OrganisationUpdateIN,
  PagedOrganisationOUT,
  UserRoleOUT,
  getLocalAuthHeader,
  organisationsApi,
  organisationsTypesApi,
  userOrganisationsApi,
} from "api-client";
import { AxiosResponse } from "axios";
import { makeAutoObservable } from "mobx";
import { buildingStore } from "../../store/Building/BuildingStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { userStore } from "../../store/UserStore";

type OrganisationRole = { id: string; name: string };
interface OrganisationsList {
  items: OrganisationOUT[];
  loading: boolean;
}

export interface Organisation extends OrganisationOUT {
  organisation_type_id?: string;
  joinRequest?: boolean;
  status?: string;
  shared?: boolean;
}

class OrganisationStore {
  organisations: OrganisationsList = {
    items: [],
    loading: true,
  };
  domainOrganisations: OrganisationOUT[] = [];
  organisationTypes: OrganisationTypeOUT[] = [];
  allRoles: OrganisationRole[] = [];

  setOrganisations(organisations: OrganisationsList) {
    this.organisations = organisations;
  }

  setDomainOrganisations(domainOrganisations: OrganisationOUT[]) {
    this.domainOrganisations = domainOrganisations;
  }

  setOrganisationTypes(organisationTypes: OrganisationTypeOUT[]) {
    this.organisationTypes = organisationTypes;
  }

  setAllRoles(allRoles: OrganisationRole[]) {
    this.allRoles = allRoles;
  }

  async getOrganisationTypes() {
    const authHeader = getLocalAuthHeader();
    return await organisationsTypesApi
      .concularApiV1RoutersOrganisationsTypeGetOrganisationTypes(
        100,
        0,
        authHeader
      )
      .then((response: AxiosResponse) =>
        this.setOrganisationTypes(response.data.items)
      )
      .catch((err) => {
        console.error(err, "OrganisationsTypeGetOrganisationTypes");
      });
  }

  async getAllOrganisations() {
    this.setOrganisations({ items: [], loading: true });
    const authHeader = getLocalAuthHeader();
    await organisationsApi
      .concularApiV1RoutersOrganisationsGetAllOrganisations(100, 0, authHeader)
      .then(async (response: AxiosResponse) => {
        const organisations = response.data.items;
        this.setOrganisations({ items: organisations, loading: false });
      })
      .catch((err) => console.error(err, "OrganisationsGetAllOrganisations"));
  }

  async getUserDomainRelatedOrganisations() {
    const authHeader = getLocalAuthHeader();
    try {
      const response: AxiosResponse<PagedOrganisationOUT, Error> =
        await organisationsApi.concularApiV1RoutersOrganisationsGetUserDomainRelatedOrganisations(
          100,
          0,
          authHeader
        );
      this.setDomainOrganisations(response.data.items);
    } catch (err) {
      console.error(err, "getUserDomainRelatedOrganisations");
    }
  }

  async getAllRoles() {
    const authHeader = getLocalAuthHeader();
    try {
      const response: AxiosResponse<UserRoleOUT[], Error> =
        await organisationsApi.concularApiV1RoutersOrganisationsGetAllRoles(
          authHeader
        );
      const roles = response.data;
      const rolesOptions = roles.map((item) => ({
        id: item.role_type,
        name: item.role_name,
      }));
      if (rolesOptions) {
        this.setAllRoles(rolesOptions);
      }
    } catch (err) {
      console.error(err, "OrganisationsGetAllRoles");
    }
  }

  async addDemoBuilding(organisationId: string) {
    const authHeader = getLocalAuthHeader();
    await organisationsApi
      .concularApiV1RoutersOrganisationsAddDemoBuilding(
        organisationId,
        authHeader
      )
      .then(async (response: AxiosResponse<BuildingOUT, Error>) => {
        const building = response.data;
        const orgBuldings = buildingStore.organisationsBuildings.find(
          (e) => e.orgId == organisationId
        );

        if (orgBuldings) {
          orgBuldings.items.push(building);
        }
      })
      .catch((err) => {
        console.error(err, "addDemoBuilding");
      });
  }

  async initialFetch() {
    await this.getOrganisationTypes();
    await this.getAllOrganisations();
    await this.getUserDomainRelatedOrganisations();
    await this.getAllRoles();
  }

  async hasOrganisationMultipleAdmins(
    organisation: OrganisationOUT | undefined
  ) {
    if (!organisation) return false;
    const authHeader = getLocalAuthHeader();
    if (!authHeader) return;
    return await userOrganisationsApi
      .concularApiV1RoutersUserOrganisationsGetNumOfAdminsForOrganisation(
        organisation.id,
        authHeader
      )
      .then((response: AxiosResponse) => {
        if (response?.data?.no_of_admins > 1) {
          return true;
        }
        return false;
      })
      .catch((err) => console.error(err));
  }

  async leaveOrganisation(organisation: OrganisationOUT) {
    const authHeader = getLocalAuthHeader();
    if (!organisation?.id || !authHeader) return;
    return await userOrganisationsApi
      .concularApiV1RoutersUserOrganisationsLeaveOrganisation(
        organisation.id,
        authHeader
      )
      .then((response) => response.data)
      .catch((err) => {
        console.error(err, "leaveOrganisation");
      });
  }

  async uploadLogo(organisationId: string, image: File) {
    const authHeader = getLocalAuthHeader();
    return await organisationsApi.concularApiV1RoutersOrganisationsUploadOrganisationLogo(
      organisationId,
      image,
      authHeader
    );
  }

  async createOrganisation(
    name: string,
    typeID: string,
    domains: string[],
    logo: File | null
  ) {
    const authHeader = getLocalAuthHeader();
    await organisationsApi
      .concularApiV1RoutersOrganisationsCreateOrganisation(
        {
          name: name,
          organisation_type_id: typeID,
          domains: domains,
        },
        authHeader
      )
      .then(async (response) => {
        const resData = response.data;
        if (logo && logo.name !== "") {
          await this.uploadLogo(resData.id, logo);
        }
        await this.getAllOrganisations();
        const createdOrganisation = this.organisations.items.find(
          (org) => org.id === response.data.id
        );
        createdOrganisation &&
          (await sidebarMenuStore.onChangeOrganisation(createdOrganisation));
        await buildingStore.getOrganisationBuildings();
        userStore.setOnboarded(this.organisations.items.length > 0);
      })
      .catch((err) => console.error(err));
  }

  async updateOrganisation(
    organisationID: string,
    options: OrganisationUpdateIN
  ) {
    const authHeader = getLocalAuthHeader();
    await organisationsApi
      .concularApiV1RoutersOrganisationsUpdateOrganisation(
        organisationID,
        options,
        authHeader
      )
      .then(async (response) => {
        await this.getAllOrganisations();
        const currentOrg = this.organisations.items.find(
          (org) => org.id === response.data.id
        );
        sidebarMenuStore.setCurrentOrganisation(currentOrg);
      })
      .catch((err) => console.error(err, "updateOrganisation"));
  }

  async uploadOrganisationLogo(organisationId: string, image: File) {
    const authHeader = getLocalAuthHeader();
    return await organisationsApi
      .concularApiV1RoutersOrganisationsUploadOrganisationLogo(
        organisationId,
        image,
        authHeader
      )
      .catch((err) => console.error(err, "UploadOrganisationLogo"));
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: OrganisationStore;

  static getInstance(): OrganisationStore {
    if (!OrganisationStore.instance) {
      OrganisationStore.instance = new OrganisationStore();
    }
    return OrganisationStore.instance;
  }
}

export const organisationStore = OrganisationStore.getInstance();
